import React from 'react';
import { useTranslation } from 'react-i18next';

import { FormLayout } from '@/components/layout';
import { LabelInput, Checkbox, TimePicker } from '@/components/ui/inputs';
import type { ScheduleBreakFormSubset } from '@/schemas/schedule/scheduleForm.schema';


interface ScheduleBreakFormProps {
  form: ScheduleBreakFormSubset;
}

const handleSubmit = (event: React.FormEvent) => {
  event.preventDefault();
};

export const ScheduleBreakForm: React.FC<ScheduleBreakFormProps> = ({ form }) => {
  const { t: translate } = useTranslation('schedule');
  const { register, watch, setValue, errors, onBreakTimeToggle } = form;

  const hasBreakTime = watch('hasBreakTime');

  return (
    <FormLayout onSubmit={handleSubmit} columns={4} data-testid="schedule-break-form">

      {/* ── Colación ──────────────────────────────────────────────────── */}
      <FormLayout.Section title={translate('form.sections.break')}>

        <Checkbox
          label={translate('form.fields.hasBreakTime')}
          checked={hasBreakTime}
          onChange={onBreakTimeToggle}
          data-testid="schedule-has-break-time-checkbox"
        />

        <Checkbox
          label={translate('form.fields.discountBreakDelay')}
          checked={watch('discountBreakDelay')}
          onChange={(checked) => setValue('discountBreakDelay', checked, { shouldDirty: true })}
          data-testid="schedule-discount-break-delay-checkbox"
        />

        <Checkbox
          label={translate('form.fields.hasBreakMarkings')}
          checked={watch('hasBreakMarkings')}
          onChange={(checked) => setValue('hasBreakMarkings', checked, { shouldDirty: true })}
          data-testid="schedule-has-break-markings-checkbox"
        />

        <LabelInput
          label={translate('form.fields.breakMinutes')}
          required={hasBreakTime}
          type="number"
          disabled={!hasBreakTime}
          registration={register('breakMinutes')}
          color={errors.breakMinutes ? 'error' : undefined}
          supportingText={
            errors.breakMinutes?.message ? translate(errors.breakMinutes.message) : undefined
          }
          data-testid="schedule-break-minutes-input"
        />

      </FormLayout.Section>

      {/* ── Hora inicio colación ──────────────────────────────────────── */}
      <FormLayout.Section title={translate('form.sections.breakStart')}>

        <TimePicker
          label={translate('form.fields.breakStartTime')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakStartTime')}
          onChange={(value) => setValue(
            'breakStartTime', value, { shouldDirty: true, shouldValidate: true },
          )}
          color={errors.breakStartTime ? 'error' : 'default'}
          supportingText={
            errors.breakStartTime?.message ? translate(errors.breakStartTime.message) : undefined
          }
          data-testid="schedule-break-start-time-picker"
        />

        <TimePicker
          label={translate('form.fields.breakEntryThresholdStart')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakEntryThresholdStart')}
          onChange={(value) => setValue('breakEntryThresholdStart', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.breakEntryThresholdStart ? 'error' : 'default'}
          supportingText={
            errors.breakEntryThresholdStart?.message
              ? translate(errors.breakEntryThresholdStart.message)
              : undefined
          }
          data-testid="schedule-break-entry-threshold-start-picker"
        />

        <TimePicker
          label={translate('form.fields.breakEntryThresholdEnd')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakEntryThresholdEnd')}
          onChange={(value) => setValue('breakEntryThresholdEnd', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.breakEntryThresholdEnd ? 'error' : 'default'}
          supportingText={
            errors.breakEntryThresholdEnd?.message
              ? translate(errors.breakEntryThresholdEnd.message)
              : undefined
          }
          data-testid="schedule-break-entry-threshold-end-picker"
        />

      </FormLayout.Section>

      {/* ── Hora fin colación ─────────────────────────────────────────── */}
      <FormLayout.Section title={translate('form.sections.breakEnd')}>

        <TimePicker
          label={translate('form.fields.breakEndTime')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakEndTime')}
          onChange={(value) => setValue(
            'breakEndTime', value, { shouldDirty: true, shouldValidate: true },
          )}
          color={errors.breakEndTime ? 'error' : 'default'}
          supportingText={
            errors.breakEndTime?.message ? translate(errors.breakEndTime.message) : undefined
          }
          data-testid="schedule-break-end-time-picker"
        />

        <TimePicker
          label={translate('form.fields.breakExitThresholdStart')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakExitThresholdStart')}
          onChange={(value) => setValue('breakExitThresholdStart', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.breakExitThresholdStart ? 'error' : 'default'}
          supportingText={
            errors.breakExitThresholdStart?.message
              ? translate(errors.breakExitThresholdStart.message)
              : undefined
          }
          data-testid="schedule-break-exit-threshold-start-picker"
        />

        <TimePicker
          label={translate('form.fields.breakExitThresholdEnd')}
          required={!hasBreakTime}
          showSeconds={false}
          disabled={hasBreakTime}
          value={watch('breakExitThresholdEnd')}
          onChange={(value) => setValue('breakExitThresholdEnd', value, { shouldDirty: true, shouldValidate: true })}
          color={errors.breakExitThresholdEnd ? 'error' : 'default'}
          supportingText={
            errors.breakExitThresholdEnd?.message
              ? translate(errors.breakExitThresholdEnd.message)
              : undefined
          }
          data-testid="schedule-break-exit-threshold-end-picker"
        />

      </FormLayout.Section>

    </FormLayout>
  );
};

export default ScheduleBreakForm;
